import { Bill } from '@enview/interface/types/bills/Bill';
import { Priority } from '@enview/interface/types/triage/Priority';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { BillTriageAPI, TeamAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';

type BulkPriorityContentProps = {
  bills: Bill[];
};

const PRIORITY_OPTIONS = [Priority.High, Priority.Medium, Priority.Low];

// Bulk Priority action: applies one priority to every selected bill within a single
// workspace — bulkUpdatePriority (the same bulk-workflow-update endpoint as
// bulkUpdateWorkflowStatus/bulkAssignBills) takes one teamId per call, so there's no
// per-bill workspace to target. Same team resolution as the column and
// BulkWorkflowStatusContent: prefers the user's active team mode when every selected
// bill belongs to it, falling back to the first workspace shared by all selected bills.
const BulkPriorityContent = (props: BulkPriorityContentProps): ReactElement => {
  const { bills } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIdSets = (bills || []).map((bill) =>
    (bill.workspaces ?? [])
      .map((workspace) => workspace.id)
      .filter((id): id is number => id !== undefined),
  );
  const sharedWorkspaceIds = billWorkspaceIdSets.length
    ? billWorkspaceIdSets.reduce((shared, ids) =>
        shared.filter((id) => ids.includes(id)),
      )
    : [];

  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    sharedWorkspaceIds,
    currentTeamModeId,
  );

  const [bulkUpdatePriority, { isLoading: priorityUpdating }] =
    BillTriageAPI.endpoints.bulkUpdatePriority.useMutation();

  // Once one priority button is clicked, every button (including itself) disables
  // until that bulk write resolves, so a second click can't fire a conflicting
  // priority onto the same bills mid-request.
  const disabled = !orgId || !teamId || !bills?.length || priorityUpdating;

  return (
    <>
      {PRIORITY_OPTIONS.map((priority) => (
        <div key={`${priority}-bulkdropdown-priority`}>
          <button
            className="dropdown-item"
            disabled={disabled}
            onClick={() =>
              orgId &&
              teamId &&
              bulkUpdatePriority({
                orgId,
                teamId,
                billIds: bills.map((bill) => bill.id),
                priority,
              })
            }
            type="button"
          >
            <span className="label-text">{priority}</span>
          </button>
        </div>
      ))}
    </>
  );
};

export default BulkPriorityContent;
